import React, { Fragment, useMemo } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Collapsible } from '@opendoor/atoms/collapsible';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import type { ListingHistoryState } from '__generated__/athena';
import { Divider } from 'components/shared/Divider';
import { formatCurrency } from 'helpers/formatters';

interface MarketplaceListingHistory {
  state?: ListingHistoryState | null;
  timestamp?: any | null;
  mls?: string | null;
  originalListPriceCents?: number | null;
  soldPriceCents?: number | null;
  currentListPriceCents?: number | null;
}

interface PriceHistoryProps {
  listingHistory: MarketplaceListingHistory[];
}

export const PriceHistory: React.FC<PriceHistoryProps> = ({ listingHistory }) => {
  // Dedupe by date after cleaning up timestamp format
  const listingHistoryByDateMap: Record<string, MarketplaceListingHistory> =
    listingHistory?.reduce(
      (map: Record<string, MarketplaceListingHistory>, history: MarketplaceListingHistory) => {
        // ex: January 10, 2023
        const date = new Date(history.timestamp).toLocaleDateString('en-us', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        if (!map[date]) {
          map[date] = { ...history, timestamp: date };
        }

        return map;
      },
      {},
    ) || {};

  // Filter out events without any pricing data, and any marketplace specific events
  const pricingEvents = Object.values(listingHistoryByDateMap)
    .filter(
      (history) =>
        history.state !== 'PRIVATELY_LISTED' && !(history.state === 'SALE_PENDING' && !history.mls),
    )
    .filter(
      (history) =>
        history?.soldPriceCents ||
        history?.currentListPriceCents ||
        history?.originalListPriceCents,
    );
  const description = useMemo(() => {
    const rows = Object.values(pricingEvents)?.map((history: MarketplaceListingHistory, index) => {
      const state = history.state as ListingHistoryState;
      const priceCents =
        state === 'SOLD' || state === 'OFF_MARKET'
          ? history?.soldPriceCents || history?.currentListPriceCents || 0
          : history?.currentListPriceCents || history?.originalListPriceCents || 0;

      return (
        <Fragment key={history.timestamp}>
          <Row
            className={cn(
              'items-center justify-between',
              index === pricingEvents.length - 1 && 'pb-6',
            )}
          >
            <Col>
              <Text className="text-body-sm text-od-content-primary">{history.timestamp}</Text>
              <Text as="span" className="text-body-sm capitalize text-od-content-secondary">
                {state?.split('_').join(' ').toLowerCase()}
                {history.mls && ` · ${history.mls}`}
              </Text>
            </Col>
            <Text as="strong" className="text-label-sm text-od-content-primary">
              {formatCurrency(priceCents)}
            </Text>
          </Row>
          {index !== pricingEvents.length - 1 && <Divider className="py-4" />}
        </Fragment>
      );
    });

    if (!rows.length) {
      return 'No price history';
    }

    return rows;
  }, [pricingEvents]);

  return (
    <Collapsible.Root defaultOpen className="w-full">
      <Collapsible.Trigger className="group flex w-full items-center justify-between gap-2 py-6">
        <Text
          as="span"
          className="text-left text-label-xl text-od-content-primary lg:text-subheader-xs"
        >
          Price history
        </Text>
        <Box className="shrink-0 text-od-content-primary transition-transform duration-200 group-data-[panel-open]:rotate-180">
          <IconChevronDown size={20} />
        </Box>
      </Collapsible.Trigger>
      <Collapsible.Panel className="pb-4">
        {typeof description === 'string' ? (
          <Text className="text-body-md text-od-content-primary">{description}</Text>
        ) : (
          description
        )}
      </Collapsible.Panel>
    </Collapsible.Root>
  );
};
